import imageCompression from 'browser-image-compression';
import {
  deleteUploadedImage,
  uploadCustomerPicture,
  uploadDocument,
  uploadDocumentByPut,
  uploadPersonPicture,
  uploadUserAvatar,
} from '../api/requests';
import {
  DELETE_IMAGE_ENDPOINTS
} from '../resources/enums';
import { blobToFile } from '../utilities/miscFunctions';

const compressOptions = {
  maxSizeMB: 5,
  maxWidthOrHeight: 1920,
  useWebWorker: true
};

function useFile() {
  const uploadImage = (image: File, url: string, requestMethod = 'post', fileName = 'file') => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image, fileName);

      if (requestMethod === 'post') {
        uploadDocument(`${url}`, imageData)
          .then((response: any) => {
            resolve(response);
          })
          .catch((error: Error) => {
            reject(error);
          });
      }

      if (requestMethod === 'put') {
        uploadDocumentByPut(`${url}`, imageData)
          .then((response: any) => {
            resolve(response);
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    });
  };

  const uploadUserProfileImage = (
    image: File
  ) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image);

      uploadUserAvatar(imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadCustomerPhoto = (
    id: string,
    image: File
  ) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image);

      uploadCustomerPicture(id, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadPersonPhoto = (
    id: string,
    image: File
  ) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image);

      uploadPersonPicture(id, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadPhoto = (
    id: string,
    image: File,
    url: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image);

      uploadCustomerPicture(id, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const getImageFormData = (image: File, imageFieldName = 'file', compress = false) => {
    const data = new FormData();

    if (compress) {
      const compressedFile = getCompressedImage(image);
      data.append(imageFieldName, blobToFile(compressedFile, image));
    } else {
      data.append(imageFieldName, image);
    }

    return data;
  };

  const getCompressedImage = async (image: File) => {
    const compressedFile = await imageCompression(image, compressOptions);

    return compressedFile;
  };

  const removeImage = (url: DELETE_IMAGE_ENDPOINTS) => {
    return new Promise<void>((resolve, reject) => {
      deleteUploadedImage(`${url}`)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const downloadFileFromBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    link.remove();
  };

  const openFileFromBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");

    // Give the browser time to load the blob before revoking it
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  };

  return {
    getCompressedImage,
    getImageFormData,
    uploadImage,
    uploadUserProfileImage,
    removeImage,
    downloadFileFromBlob,
    uploadCustomerPhoto,
    uploadPhoto,
    uploadPersonPhoto,
    openFileFromBlob
  };
}

export default useFile;
